// Stock Car Detail — full-screen investment recovery for one purchased car.
// A car has many parts, so the per-car view is a page (not a cramped modal):
// vehicle header + the full investment-recovery UI (progress, every part,
// add / delete sale).

import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  FiArrowLeft,
  FiTruck,
  FiCalendar,
  FiDollarSign,
  FiRefreshCw,
} from 'react-icons/fi';
import MainLayout from '../components/layout/MainLayout.jsx';
import { InvestmentRecovery, formatPrice } from '../components/stock/InvestmentRecovery.jsx';
import { fetchStockDetail, clearStockDetail } from '../redux/slices/stockSlice';

const errorMessage = (error) => {
  if (!error) return null;
  if (typeof error === 'string') return error;
  if (Array.isArray(error)) return error.join(', ');
  if (error.detail || error.message) return error.detail || error.message;
  return Object.entries(error)
    .map(([field, value]) => `${field.replace(/_/g, ' ')}: ${Array.isArray(value) ? value.join(', ') : value}`)
    .join(' · ');
};

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { detail, detailLoading, detailError } = useSelector((s) => s.stocks);

  useEffect(() => {
    dispatch(fetchStockDetail(id));
    return () => { dispatch(clearStockDetail()); };
  }, [dispatch, id]);

  const stock = detail;

  return (
    <MainLayout>
      <div className="space-y-5 pb-8">

        {/* ── Header bar ───────────────────────────────────────────────────── */}
        <div className="flex items-center gap-3 animate-fade-in-up">
          <button
            onClick={() => navigate('/products')}
            className="w-9 h-9 rounded-xl bg-white border border-slate-200 shadow-sm flex items-center justify-center text-slate-500 hover:text-violet-600 hover:border-violet-300 hover:bg-violet-50 transition-all shrink-0"
          >
            <FiArrowLeft className="w-4 h-4" />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-bold text-slate-900 truncate sm:text-xl">
              {stock ? stock.car_name : 'Stock details'}
            </h1>
            <p className="text-sm text-slate-500 truncate">
              {stock
                ? ([stock.model, stock.year].filter(Boolean).join(' · ') || `Stock #${stock.id}`)
                : (detailLoading ? 'Loading…' : ' ')}
            </p>
          </div>
          <button
            onClick={() => dispatch(fetchStockDetail(id))}
            disabled={detailLoading}
            title="Refresh"
            className="w-9 h-9 rounded-xl bg-white border border-slate-200 shadow-sm flex items-center justify-center text-slate-400 hover:text-violet-600 hover:border-violet-300 hover:bg-violet-50 transition-all disabled:opacity-50 shrink-0"
          >
            <FiRefreshCw className={`w-4 h-4 ${detailLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* ── Loading ──────────────────────────────────────────────────────── */}
        {detailLoading && !stock && (
          <div className="flex flex-col items-center gap-3 py-24">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-violet-500 border-t-transparent" />
            <p className="text-sm text-slate-400">Loading stock details…</p>
          </div>
        )}

        {/* ── Error ────────────────────────────────────────────────────────── */}
        {detailError && !detailLoading && (
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-center">
            <p className="text-rose-700 font-semibold mb-1">Failed to load stock</p>
            <p className="text-rose-500 text-sm mb-4">{errorMessage(detailError)}</p>
            <button
              onClick={() => dispatch(fetchStockDetail(id))}
              className="px-4 py-2 bg-rose-600 text-white text-sm font-semibold rounded-xl hover:bg-rose-700 transition-all"
            >
              Try Again
            </button>
          </div>
        )}

        {stock && (
          <div className="space-y-5">
            {/* Vehicle hero */}
            <div className="rounded-2xl bg-gradient-to-br from-violet-600 to-indigo-700 p-5 text-white sm:p-6 animate-fade-in-up">
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-white/15">
                <FiTruck className="h-5 w-5" />
              </div>
              <p className="text-xs font-semibold uppercase tracking-wider text-violet-200">Vehicle stock · #{stock.id}</p>
              <h2 className="mt-1 text-2xl font-bold">{stock.car_name}</h2>
              <p className="mt-1 text-sm text-violet-100">{[stock.model, stock.year].filter(Boolean).join(' · ') || '—'}</p>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {stock.rc_status && (
                  <span className="inline-flex items-center rounded-full bg-white/15 px-2.5 py-0.5 text-xs font-semibold text-white">
                    {stock.rc_status_display || (stock.rc_status === 'with_rc' ? 'With RC' : 'Without RC')}
                  </span>
                )}
                {stock.variant && (
                  <span className="inline-flex items-center rounded-full bg-white/15 px-2.5 py-0.5 text-xs font-semibold text-white">
                    {stock.variant_display || stock.variant.toUpperCase()}
                  </span>
                )}
              </div>
            </div>

            {/* Key facts */}
            <div className="grid gap-3 sm:grid-cols-2 animate-fade-in-up" style={{ animationDelay: '0.05s' }}>
              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="mb-1 flex items-center gap-1.5 text-xs text-slate-400"><FiCalendar className="h-3.5 w-3.5" /> Purchase date</p>
                <p className="font-semibold text-slate-800">{stock.purchase_date_display || stock.purchase_date || '—'}</p>
              </div>
              <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4 shadow-sm">
                <p className="mb-1 flex items-center gap-1.5 text-xs text-emerald-600"><FiDollarSign className="h-3.5 w-3.5" /> Purchase price (investment)</p>
                <p className="text-lg font-bold text-emerald-700">₹{formatPrice(stock.purchase_price)}</p>
              </div>
            </div>

            {/* Investment recovery — full width, every part off this car */}
            <div className="animate-fade-in-up" style={{ animationDelay: '0.08s' }}>
              <InvestmentRecovery stock={stock} />
            </div>

            {/* Description + notes */}
            {(stock.description || stock.notes) && (
              <div className="grid gap-3 sm:grid-cols-2 animate-fade-in-up" style={{ animationDelay: '0.10s' }}>
                {stock.description && (
                  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                    <p className="mb-1 text-xs text-slate-400">Description</p>
                    <p className="text-sm text-slate-700">{stock.description}</p>
                  </div>
                )}
                {stock.notes && (
                  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                    <p className="mb-1 text-xs text-slate-400">Notes</p>
                    <p className="text-sm text-slate-700">{stock.notes}</p>
                  </div>
                )}
              </div>
            )}

            {(stock.created_at || stock.updated_at) && (
              <div className="text-xs text-slate-400">
                {stock.created_at && <p>Created: {stock.created_at}</p>}
                {stock.updated_at && <p className="mt-1">Updated: {stock.updated_at}</p>}
              </div>
            )}
          </div>
        )}

      </div>
    </MainLayout>
  );
};

export default ProductDetailPage;
